/**
 * Conditional Access API Service
 *
 * Provides methods for managing Conditional Access policies.
 * Supports creating, listing, and managing policies for passwordless
 * enforcement and legacy authentication blocking.
 *
 * Uses Bearer token authentication via MSAL through ApiService.
 */

import { ApiService } from './api';
import type {
  ConditionalAccessPolicyCreateRequest,
  ConditionalAccessPolicyResponse,
} from '../types/conditionalAccess';

/**
 * Conditional Access API Client
 *
 * Follows the same pattern as SessionControlsApiService for consistency.
 * All methods automatically include Authorization header via ApiService.
 */
class ConditionalAccessApiServiceClass {
  /**
   * Get base URL for conditional access policy endpoints.
   *
   * @param tenantId - Azure AD tenant identifier
   * @returns Base API URL
   */
  private getBaseUrl(tenantId: string): string {
    return `/api/tenants/${tenantId}/security/policies/conditional-access`;
  }

  /**
   * Create a new Conditional Access policy.
   *
   * Creates either a passwordless enforcement policy or a legacy
   * authentication blocking policy based on the policy_type field.
   *
   * @param tenantId - Azure AD tenant identifier
   * @param request - Policy creation request
   * @returns Created policy details
   * @throws Error if policy creation fails
   */
  async createConditionalAccessPolicy(
    tenantId: string,
    request: ConditionalAccessPolicyCreateRequest
  ): Promise<ConditionalAccessPolicyResponse> {
    const response = await ApiService.request<ConditionalAccessPolicyResponse>(
      `${this.getBaseUrl(tenantId)}/policies`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );

    if (!response) {
      throw new Error('Empty response from server');
    }

    return response;
  }

  /**
   * Update an existing Conditional Access policy.
   *
   * Updates policy configuration including session controls,
   * user exclusions, and policy state.
   *
   * @param tenantId - Azure AD tenant identifier
   * @param policyId - Conditional Access policy ID
   * @param request - Policy update request
   * @returns Updated policy details
   * @throws Error if policy update fails
   */
  async updateConditionalAccessPolicy(
    tenantId: string,
    policyId: string,
    request: ConditionalAccessPolicyCreateRequest
  ): Promise<ConditionalAccessPolicyResponse> {
    const response = await ApiService.request<ConditionalAccessPolicyResponse>(
      `${this.getBaseUrl(tenantId)}/policies/${policyId}`,
      {
        method: 'PUT',
        body: JSON.stringify(request),
      }
    );

    if (!response) {
      throw new Error('Empty response from server');
    }

    return response;
  }

  /**
   * List all Conditional Access policies for a tenant.
   *
   * Retrieves all CA policies configured for the tenant, including
   * enabled and disabled policies.
   *
   * @param tenantId - Azure AD tenant identifier
   * @returns Array of Conditional Access policies
   * @throws Error if request fails
   */
  async listConditionalAccessPolicies(
    tenantId: string
  ): Promise<ConditionalAccessPolicyResponse[]> {
    const response = await ApiService.request<ConditionalAccessPolicyResponse[]>(
      `${this.getBaseUrl(tenantId)}/policies`,
      {
        method: 'GET',
      }
    );

    return response;
  }

  /**
   * Delete a Conditional Access policy.
   *
   * Permanently removes the policy from the tenant. This action
   * cannot be undone.
   *
   * @param tenantId - Azure AD tenant identifier
   * @param policyId - Conditional Access policy ID to delete
   * @returns Success message
   * @throws Error if policy not found or deletion fails
   */
  async deleteConditionalAccessPolicy(
    tenantId: string,
    policyId: string
  ): Promise<{ message: string }> {
    const response = await ApiService.request<{ message: string }>(
      `${this.getBaseUrl(tenantId)}/policies/${policyId}`,
      {
        method: 'DELETE',
      }
    );

    return response;
  }
}

export const ConditionalAccessApiService = new ConditionalAccessApiServiceClass();
